import { type Control, type FieldValues, type Path, useController } from 'react-hook-form'
import { StyleSheet, View, type ViewProps } from 'react-native'
import { HelperText } from 'react-native-paper'
import DateTimePicker from '@/components/DateTimePicker'
import { dateToTime, timeToDate } from '@/lib/working-hours'

export type TimeInputProps<T extends FieldValues = Record<string, unknown>> = {
    name: Path<T>
    control: Control<T>
    label: string
    hint?: string
    disabled?: boolean
    wrapperProps?: ViewProps
}

export function TimeInput<T extends FieldValues = Record<string, unknown>>({
    name,
    control,
    label,
    hint,
    disabled,
    wrapperProps = {},
}: TimeInputProps<T>) {
    const {
        field,
        fieldState: { error },
    } = useController({
        name,
        control,
    })

    const handleChange = (date: Date | null) => {
        if (date) {
            field.onChange(dateToTime(date))
        }
    }

    const value = field.value ? timeToDate(field.value) : new Date()

    return (
        <View style={[styles.inputWrapper, wrapperProps.style]} {...wrapperProps}>
            <DateTimePicker
                value={value}
                onChange={handleChange}
                mode="time"
                label={label}
                disabled={disabled}
                minuteInterval={15}
                error={!!error}
            />
            {hint && !error ? <HelperText type="info">{hint}</HelperText> : null}
            {error ? <HelperText type="error">{error.message}</HelperText> : null}
        </View>
    )
}

const styles = StyleSheet.create({
    inputWrapper: {
        marginBottom: 8,
    },
})
